import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Activity, Users, Zap, RefreshCw, Loader2 } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { LAYOUT } from '@/design_tokens';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HealthChip from './HealthChip';
import UserEngagementDrawer from './UserEngagementDrawer';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { formatDistanceToNow } from 'date-fns';

const COHORT_LABELS = {
  healthy: 'Healthy',
  at_risk: 'At risk',
  dormant: 'Dormant',
  never_activated: 'Never activated',
};

const fmtRel = (iso) => {
  if (!iso) return 'never';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return 'never'; }
};

export default function EngagementPage() {
  const [overview, setOverview] = useState(null);
  const [atRiskTrials, setAtRiskTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerUserId, setDrawerUserId] = useState(null);

  const load = async (opts = {}) => {
    const { signal } = opts;
    try {
      setLoading(true);
      const [ov, ar] = await Promise.all([
        api.get('/admin/engagement/overview', { signal }),
        api.get('/admin/engagement/at-risk-trials?days_left=7', { signal }),
      ]);
      setOverview(ov.data);
      setAtRiskTrials(ar.data.trials);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      toast.error(e.response?.data?.detail || 'Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, []);

  const cohortRows = useMemo(() => {
    if (!overview) return [];
    return Object.entries(overview.cohorts).map(([status, count]) => ({
      status,
      label: COHORT_LABELS[status],
      count,
      pct: overview.total_users > 0 ? (count / overview.total_users) * 100 : 0,
      trial: overview.trial_cohorts?.[status] ?? 0,
      active: overview.active_cohorts?.[status] ?? 0,
    }));
  }, [overview]);

  return (
    <div className={LAYOUT.wideContainer} data-testid="engagement-page">
      <PageHeader
        title="User Engagement"
        subtitle="See who's actively using the platform, who's at risk, and where users drop off in the onboarding funnel."
        icon={Activity}
        testId="engagement-header"
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="engagement-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''} mr-2`} />
            Refresh
          </Button>
        }
      />

      {loading && !overview ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : !overview ? null : (
        <div className="space-y-6">
          {/* DAU/WAU/MAU strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { label: 'DAU (24h)', value: overview.dau, testId: 'stat-dau' },
              { label: 'WAU (7d)', value: overview.wau, testId: 'stat-wau' },
              { label: 'MAU (30d)', value: overview.mau, testId: 'stat-mau' },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="p-4">
                  <div className="text-xs text-slate-400 uppercase tracking-wide">{s.label}</div>
                  <div className="text-3xl font-bold text-slate-100 mt-1" data-testid={s.testId}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">meaningful actions in window</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Cohort breakdown */}
          <Card data-testid="cohort-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Health Cohorts</CardTitle>
              <CardDescription>
                All non-admin, non-deleted users bucketed by activity signals + signal ownership.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {cohortRows.map((r) => (
                  <div key={r.status} className="flex items-center gap-3" data-testid={`cohort-row-${r.status}`}>
                    <div className="w-32"><HealthChip status={r.status} /></div>
                    <div className="flex-1 h-6 bg-slate-800 rounded overflow-hidden">
                      <div
                        className={
                          r.status === 'healthy' ? 'h-full bg-emerald-500/60' :
                          r.status === 'at_risk' ? 'h-full bg-amber-500/60' :
                          r.status === 'dormant' ? 'h-full bg-rose-500/60' :
                          'h-full bg-slate-500/60'
                        }
                        style={{ width: `${Math.max(2, r.pct)}%` }}
                      />
                    </div>
                    <div className="w-28 text-right text-sm text-slate-200">
                      <span className="font-semibold" data-testid={`cohort-count-${r.status}`}>{r.count}</span>
                      <span className="text-slate-500 text-xs ml-1">({r.pct.toFixed(0)}%)</span>
                    </div>
                    <div className="w-32 text-right text-xs text-slate-500">
                      {r.trial}T · {r.active}A
                    </div>
                  </div>
                ))}
                <div className="text-[11px] text-slate-500 mt-1">
                  T = trial users · A = active paying subs (of that health bucket)
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DAU line chart */}
          <Card data-testid="dau-chart-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Zap className="h-4 w-4" /> Daily Active Users (30d)</CardTitle>
              <CardDescription>Distinct users taking a meaningful action per day.</CardDescription>
            </CardHeader>
            <CardContent>
              {(overview.dau_series_30d?.length || 0) === 0 ? (
                <div className="h-56 flex items-center justify-center text-sm text-slate-400" data-testid="dau-chart-empty">
                  No engagement events yet — tracking starts from now.
                </div>
              ) : (
                <div className="h-56" data-testid="dau-chart">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={overview.dau_series_30d} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                      <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#64748b"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        minTickGap={30}
                      />
                      <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1f2937', borderRadius: 8 }} />
                      <Line type="monotone" dataKey="dau" stroke="#60a5fa" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Adoption funnel */}
          <Card data-testid="funnel-card">
            <CardHeader>
              <CardTitle>Adoption Funnel</CardTitle>
              <CardDescription>
                What share of users have crossed each first-step threshold.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {overview.adoption_funnel.steps.map((step) => {
                  const pct = overview.adoption_funnel.total > 0
                    ? (step.count / overview.adoption_funnel.total) * 100 : 0;
                  return (
                    <div key={step.key} className="flex items-center gap-3" data-testid={`funnel-step-${step.key}`}>
                      <div className="w-52 text-sm text-slate-200">{step.label}</div>
                      <div className="flex-1 h-4 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full bg-blue-500/50" style={{ width: `${Math.max(2, pct)}%` }} />
                      </div>
                      <div className="w-28 text-right text-sm text-slate-200">
                        <span className="font-semibold">{step.count}</span>
                        <span className="text-slate-500 text-xs ml-1">({pct.toFixed(0)}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Trials at risk */}
          <Card data-testid="trials-at-risk-card">
            <CardHeader className="flex-row items-start justify-between">
              <div>
                <CardTitle>Trials at risk (next 7 days)</CardTitle>
                <CardDescription>
                  Trials expiring within a week that show low engagement — prime candidates for a save-the-trial email.
                </CardDescription>
              </div>
              <Badge variant="secondary" className="bg-amber-900/30 text-amber-200" data-testid="at-risk-total">
                {atRiskTrials.length}
              </Badge>
            </CardHeader>
            <CardContent>
              {atRiskTrials.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">No at-risk trials. 🎉</div>
              ) : (
                <div className="divide-y divide-slate-800 border border-slate-700/60 rounded-lg">
                  {atRiskTrials.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setDrawerUserId(t.id)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800/40 flex items-center justify-between"
                      data-testid={`at-risk-row-${t.id}`}
                    >
                      <div>
                        <div className="text-sm text-slate-100">{t.email}</div>
                        <div className="text-xs text-slate-500">
                          Signals: {t.signal_count} · Last active {fmtRel(t.last_active_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-rose-900/30 text-rose-200">
                          {t.days_left != null ? `${t.days_left}d left` : 'expiring'}
                        </Badge>
                        <HealthChip status={t.health_status} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <UserEngagementDrawer
        userId={drawerUserId}
        open={!!drawerUserId}
        onOpenChange={(v) => !v && setDrawerUserId(null)}
      />
    </div>
  );
}
